import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Skull, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { EventCard } from "@/components/EventCard";

const yr = (y) => (y < 0 ? `${-y} BC` : y);
const personName = (t) => (t || "").replace(/^(Birth|Death) of /i, "").replace(/\s+(born|dies|died|is born)$/i, "").trim();

/** People who actually died on this date: our verified deaths (infamous first) topped up with Wikipedia's deaths for the day. */
export const DiedOnThisDay = ({ hl, dateLabel = "today" }) => {
  const [faces, setFaces] = useState({});
  const ours = useMemo(() => (hl?.deaths || []).slice(0, 4), [hl]);
  useEffect(() => {
    if (!ours.length) return;
    const names = ours.map((e) => personName(e.event_title));
    api.get("/portraits", { params: { names: names.join("|") } }).then((r) => setFaces(r.data.portraits || {})).catch(() => setFaces({}));
  }, [ours]);
  if (!hl) return null;
  const withFace = (e) => ({ ...e, portrait: faces[personName(e.event_title)]?.image || null });
  const seen = new Set((hl.deaths || []).map((e) => personName(e.event_title).toLowerCase()));
  const wiki = (hl.wiki_deaths || []).filter((w) => !seen.has((w.page?.title || "").toLowerCase())).slice(0, Math.max(0, 8 - Math.min(ours.length, 4)));
  if (ours.length === 0 && wiki.length === 0) return null;
  return (
    <section id="deaths" data-testid="deaths-section" className="mt-10">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Skull className="w-5 h-5 text-ink-muted" />
          <h2 className="text-2xl font-bold text-ink">Died on this day</h2>
          <span data-testid="deaths-count" className="ml-1 text-[12px] font-semibold text-ink-muted">{ours.length + wiki.length} {dateLabel}</span>
        </div>
        <p className="text-[13px] text-ink-muted">Everyone here died on {dateLabel === "today" ? "this day" : dateLabel}. Executions, assassinations and murders through history: <Link to="/died" data-testid="died-all-link" className="font-semibold text-gold hover:underline">Died on this day →</Link></p>
      </div>
      {ours.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="deaths-grid">
          {ours.map((e, i) => (
            <div key={e.id} className="relative" data-testid={`death-card-${i + 1}`}>
              {e.death_cause && <span className="absolute -top-2 left-4 z-10 px-2.5 py-0.5 rounded-full bg-ink text-gold text-[10px] font-bold uppercase tracking-wider shadow-sm">{e.death_cause}</span>}
              <EventCard e={withFace(e)} i={i} total={ours.length} />
            </div>
          ))}
        </div>
      )}
      {wiki.length > 0 && (
        <ul className={`${ours.length ? "mt-3" : ""} grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3`} data-testid="deaths-wiki">
          {wiki.map((w, i) => (
            <li key={w.url || i} data-testid={`death-wiki-${i + 1}`} className="flex gap-3 bg-white border border-[#e5e7eb] rounded-xl p-3">
              {(typeof w.image === "string" ? w.image : w.image?.source || w.page?.thumbnail) ? <img src={typeof w.image === "string" ? w.image : w.image?.source || w.page?.thumbnail} alt="" loading="lazy" className="w-12 h-12 rounded-full object-cover shrink-0 ring-1 ring-ink/10" /> : <span className="w-12 h-12 rounded-full bg-paper shrink-0 inline-flex items-center justify-center text-ink-muted"><Skull className="w-5 h-5" /></span>}
              <div className="min-w-0">
                <p className="text-[12px] font-bold text-gold leading-none">{w.year}</p>
                <p className="mt-1 text-[13px] text-ink leading-snug line-clamp-3">{w.text}</p>
                {w.url && <a href={w.url} target="_blank" rel="noopener noreferrer" className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-ink-muted hover:text-gold"><ExternalLink className="w-3 h-3" /> Wikipedia</a>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
